/**
 * Food Distribution Types
 */

export interface FDClient {
  fdClientId: number;
  clientUid: string;
  firstName: string;
  lastName: string;
  phone: string;
  zipCode: string;
  householdSize: number;
  childrenInHousehold: number;
  serviceNeeds?: string | null;
  tefapEligibility?: string[] | null;
  individualId?: number | null;
  crmClientId?: string | null;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface FDClientLookupResult {
  fdClientId: number;
  firstName: string;
  lastInitial: string;
  hasTefapEligibility?: boolean;
  source?: 'fd' | 'crm';
  checkedInToday?: boolean;
}

export interface FDIntakePreFillData {
  firstName: string;
  lastName: string;
  phoneNumber: string;
  zipCode: string;
  city: string;
  fdClientId: number;
}

export interface FDVisit {
  fdVisitId: number;
  fdClientId: number;
  visitDatetime: string;
  visitSequence: number;
  distributionType: string;
  usdaHh3?: string;
  usdaHh4?: string;
  usdaAd1?: string;
  usdaAd1a?: number | null;
  usdaAd1aDontknow?: boolean;
  usdaAd2?: string;
  usdaAd3?: string;
  usda6RawScore?: number;
  usda6Category?: string;
  fruitvegDays?: number | null;
  fruitvegDontknow?: boolean;
  serviceNeeds?: string[] | null;
  staffUser?: string;
  notes?: string;
  isSameDayOverride?: boolean;
  createdAt: string;
}

export interface FDReferral {
  fdReferralId: number;
  fdClientId: number;
  fdVisitId?: number | null;
  referralType: string;
  referredDate: string;
  engagementStatus: string;
  engagementDate?: string | null;
  completionDate?: string | null;
  staffNotes?: string | null;
  notesUpdatedBy?: string | null;
  createdAt: string;
  updatedAt: string;
  // Joined fields from client
  clientName?: string;
  firstName?: string;
  lastName?: string;
  clientUid?: string;
}

export interface FDCreateClientRequest {
  firstName: string;
  lastName: string;
  phone: string;
  zipCode: string;
  householdSize: number;
  childrenInHousehold: number;
  tefapEligibility?: string[];
}

export interface FDCreateVisitRequest {
  fdClientId: number;
  distributionType: string;
  usdaHh3?: string;
  usdaHh4?: string;
  usdaAd1?: string;
  usdaAd1a?: number | null;
  usdaAd1aDontknow?: boolean;
  usdaAd2?: string;
  usdaAd3?: string;
  fruitvegDays?: number | null;
  fruitvegDontknow?: boolean;
  serviceNeeds?: string[];
  referralTypes?: string[];
  tefapEligibility?: string[];
  bypassDuplicateWarning?: boolean;
}

export interface FDRegisterAndVisitRequest {
  firstName: string;
  lastName: string;
  phone: string;
  zipCode: string;
  householdSize: number;
  childrenInHousehold: number;
  distributionType: string;
  usdaHh3?: string;
  usdaHh4?: string;
  usdaAd1?: string;
  usdaAd1a?: number | null;
  usdaAd1aDontknow?: boolean;
  usdaAd2?: string;
  usdaAd3?: string;
  fruitvegDays?: number | null;
  fruitvegDontknow?: boolean;
  referralTypes?: string[];
  tefapEligibility?: string[];
  bypassDuplicateWarning?: boolean;
}

export interface FDRegisterAndVisitResponse {
  client: FDClient;
  visit: FDVisit;
  referrals: FDReferral[];
}

export interface FDClientProgressRecord {
  fdClientId: number;
  clientUid: string;
  firstName: string;
  lastName: string;
  zipCode: string;
  householdSize: number;
  visitCount: number;
  firstVisitDate: string;
  firstUsdaScore: number | null;
  firstUsdaCategory: string | null;
  firstFruitvegDays: number | null;
  latestVisitDate: string;
  latestUsdaScore: number | null;
  latestUsdaCategory: string | null;
  latestFruitvegDays: number | null;
  usdaScoreChange: number | null;
  fruitvegDaysChange: number | null;
}

export interface FDClientFilters {
  searchQuery?: string;
  isActive?: boolean;
  page?: number;
  pageSize?: number;
  sortBy?: string;
  sortDir?: 'asc' | 'desc';
  zipCode?: string;
}

export interface FDVisitFilters {
  page?: number;
  pageSize?: number;
}

export interface FDReferralFilters {
  engagementStatus?: string;
  referralType?: string;
  page?: number;
  pageSize?: number;
  sortBy?: string;
  sortDir?: 'asc' | 'desc';
}

export interface FDReportParams {
  startDate?: string;
  endDate?: string;
}

export const TEFAP_ELIGIBILITY_OPTIONS = [
  'Income eligibility',
  'Supplemental Nutrition Assistance Program (SNAP)',
  'Temporary Assistance to Needy Families (TANF)',
  'Supplemental Security Income (SSI)',
  'Medicaid',
] as const;

export type TefapEligibilityOption = (typeof TEFAP_ELIGIBILITY_OPTIONS)[number];

export const SERVICE_NEEDS_OPTIONS = [
  'Baby Closet',
  'Food Pantry / Food Co-op',
  'SNAP / WIC / Benefits Enrollment',
  'Job Assistance / Job Training',
  'Healthcare',
  'Mental Health Services',
  'Education / GED',
  'Childcare',
  'Housing / Rental Assistance',
  'After-school Programming',
  'Athletics / Sports Enrollment',
] as const;

export const REFERRAL_TYPE_OPTIONS = [
  'SNAP / WIC / Benefits Enrollment',
  'Job Assistance / Job Training',
  'Healthcare',
  'Mental Health Services',
  'Education / GED',
  'Childcare',
  'Housing / Rental Assistance',
  'Baby Closet',
  'After-school Programming',
  'Athletics / Sports Enrollment',
] as const;

export type ServiceNeedOption = (typeof SERVICE_NEEDS_OPTIONS)[number];
export type ReferralTypeOption = (typeof REFERRAL_TYPE_OPTIONS)[number];
